import { createContext, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { SubscriptionsState } from 'src/@types/feature';
import useMerchant from '../hooks/useMerchant';
import { updatePaymentMethodAPI } from 'src/resources/api/subscription/index.api';
import { useQueryMerchantFeaturesGet } from 'src/resources/queries/features.queries';
import {
  useMutationSaasSubCancel,
  useMutationSaasSubCreate,
  useMutationSaasSubUpdate,
  useQueryMerchantSubscriptionGet,
  useQuerySubscriptionsList
} from 'src/resources/queries';
// import { appcuesIdentifyMerchantSubscriptions, appcuesTrackEvent } from "../utils/appcues";
import useAuth from 'src/hooks/useAuth';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { BASE_URL } from "src/config";
import { DEFAULT_CURRENCY } from 'src/utils/priceFormatter';
import { MerchantSubscription } from 'src/resources/api/subscription/types.api';

const SubscriptionsContext = createContext<SubscriptionsState>({} as SubscriptionsState);

type SubscriptionsProviderProps = {
  children: ReactNode;
};

//

export enum PackageCodes {
  EVENT_OFFSETTING = 'EVENT_OFFSETTING',
  BUSINESS_OFFSETTING = 'BUSINESS_OFFSETTING',
  TRAVEL_TOURS = 'TRAVEL_TOURS',
  ACCOMMODATION_OFFSETTING = 'ACCOMMODATION_OFFSETTING',
  AVIATION = "AVIATION",
}

// Note: prefer the item with packageCode first
export const selectCurrentPlan = (subscriptions?: MerchantSubscription[]) =>
  subscriptions?.find((el) => el?.id && el?.packageCode) ?? subscriptions?.find((el) => el?.id);

//

const SubscriptionsContextProvider = ({ children }: SubscriptionsProviderProps) => {
  const [planCurrency, setPlanCurrency] = useState('');

  const { merchant, isMerchantLoading } = useMerchant();
  const { user } = useAuth();

  const getMerchantFeaturesHook = useQueryMerchantFeaturesGet({
    variables: { merchantCode: merchant?.code! },
    enabled: !!merchant?.code,
    staleTime: 0,
    gcTime: 0,
  });

 const features = useMemo(
    () => getMerchantFeaturesHook?.data?.features || [],
    [getMerchantFeaturesHook?.data?.features]
  );

  const getAllSubscriptionHook = useQuerySubscriptionsList({
    variables: { merchantCode: merchant?.code || '' },
    enabled: !!merchant?.code,
  });

  // EVENT_OFFSETTING always goes first; the rest keep the API order
  const subscriptionPlans = useMemo(() => {
    const plans = getAllSubscriptionHook?.data?.content || [];
    return [...plans].sort((a, b) =>
      Number(b?.planPackageCode === PackageCodes.EVENT_OFFSETTING) -
      Number(a?.planPackageCode === PackageCodes.EVENT_OFFSETTING)
    );
  }, [getAllSubscriptionHook?.data?.content]);

  const availablePlanCurrencies = useMemo(() => {
    const list = subscriptionPlans?.[0]?.prices;
    return [...new Set(list?.map((item) => item?.currency?.toUpperCase()))];
  }, [subscriptionPlans]);

  const getMerchantSubscriptionHook = useQueryMerchantSubscriptionGet({
    variables: { merchantCode: merchant?.code! },
    enabled: !!merchant?.code,
    staleTime: 0,
    gcTime: 0,
  });
  
  const queryClient = useQueryClient();
  
  // The provider stays mounted for the whole session, so navigating between pages never triggers
  // a refetch on its own. Pages that must show the latest subscription call this on mount.
  const refetchSubscriptions = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: useQueryMerchantFeaturesGet.getKey() });
    queryClient.invalidateQueries({ queryKey: useQueryMerchantSubscriptionGet.getKey() });
  }, [queryClient]);

  // TODO: replace with broadcastQueryClient from @tanstack/query-broadcast-client-experimental once it's stable
  useEffect(() => {
    const channel = new BroadcastChannel('subscription-changes');
    channel.onmessage = () => refetchSubscriptions();
    return () => channel.close();
  }, [refetchSubscriptions]);

  const merchantSubscriptions = useMemo(() => {
    return getMerchantSubscriptionHook?.data || {
      currentSubscriptions: [],
      expiredSubscriptions: [],
      trialsAvailable: []
    };
  }, [getMerchantSubscriptionHook?.data]);

  const currentPlan = useMemo(
    () => selectCurrentPlan(merchantSubscriptions?.currentSubscriptions),
    [merchantSubscriptions]
  );

  useEffect(() => {
    if (merchant) {
      if (currentPlan?.price?.currency) {
        setPlanCurrency(currentPlan?.price?.currency?.toLocaleUpperCase());
      } else if (availablePlanCurrencies.includes(merchant?.currency)) {
        setPlanCurrency(merchant.currency);
      } else {
        setPlanCurrency(DEFAULT_CURRENCY);
      }
    }
  }, [currentPlan, merchant, isMerchantLoading, availablePlanCurrencies, availablePlanCurrencies?.length]);

  const getPlanByPlanLevel = (packageCode: string) => {
    if (subscriptionPlans) {
      return subscriptionPlans?.find(plan => plan?.planPackageCode === packageCode);
    }
  }

  const createCheckoutSession = useMutationSaasSubCreate({
    onSuccess: (data) => window.location.href = data.checkoutUrl,
  });

  const updateSaasSubscription = useMutationSaasSubUpdate({
    onSuccess: (data) => window.location.href = data.url,
  });

  const cancelSubscription = useMutationSaasSubCancel({
    onSuccess: (data) => window.location.href = data.url,
  });

  // Snapshot the subscription being replaced so the confirmation page can tell
  // "the old subscription is still there" apart from "the new one is active" —
  // every subscription (including legacy ones) already has an id before checkout
  // even starts, so presence of an id alone can't signal completion.
  const buildConfirmationSuccessUrl = () => {
    if (!currentPlan?.id) return `${BASE_URL}${PATH_DASHBOARD.account.confirmation}`;

    const params = new URLSearchParams({ prevId: currentPlan.id });
    if (currentPlan.packageCode) params.set('prevPackageCode', currentPlan.packageCode);
    if (currentPlan.recurringInterval) params.set('prevInterval', currentPlan.recurringInterval);

    return `${BASE_URL}${PATH_DASHBOARD.account.confirmation}?${params.toString()}`;
  };

  const onSubscribeButtonClick = (planId: string) => {
    if(!user?.email || !merchant?.code || !planId) {
      return;
    }

    if (!merchantSubscriptions?.currentSubscriptions?.filter(sub => !sub.planLevel).length) {
      createCheckoutSession.mutate({
        merchantCode: merchant?.code,
        customerEmail: user.email,
        priceId: planId,
        successUrl: buildConfirmationSuccessUrl(),
        cancelUrl: `${BASE_URL}${PATH_DASHBOARD.account.plans}`
      })
    } else {
      updateSaasSubscription.mutate({
        merchantCode: merchant?.code,
        priceId: planId,
        successUrl: buildConfirmationSuccessUrl(),
        cancelUrl: `${BASE_URL}${PATH_DASHBOARD.account.plans}`
      })
    }
  }

  const onUpdateToAnnualButtonClick = (packageCode: string, currency: string, interval: string) => {
    const planPrices = getPlanByPlanLevel(packageCode)?.prices;
    const yearlyPlanId = planPrices?.find(price =>
      price?.recurringInterval === interval && price?.currency === currency?.toLowerCase());

    if(!user?.email || !merchant?.code || !yearlyPlanId) {
      return;
    }

    const data = {
      priceId: yearlyPlanId.id,
      successUrl: buildConfirmationSuccessUrl(),
      cancelUrl: `${BASE_URL}${PATH_DASHBOARD.account.plans}`
    }

    updateSaasSubscription.mutate({ merchantCode: merchant?.code, ...data })
  }

  const onCancelButtonClick = () => {
    if(!user?.email || !merchant?.code) {
      return;
    }
    cancelSubscription.mutate({
      merchantCode: merchant?.code,
      successUrl: `${BASE_URL}${PATH_DASHBOARD.account.cancelConfirmation}`,
      cancelUrl: `${BASE_URL}${PATH_DASHBOARD.account.plans}`
    });
  }

  const onUpdatePaymentMethod = async (): Promise<void> => {
    if(user?.email && merchant?.code) {
      const request = {
        successUrl: `${BASE_URL}${PATH_DASHBOARD.account.confirmation}`,
        cancelUrl: `${BASE_URL}${PATH_DASHBOARD.account.plans}`
      }
      const { data } = await updatePaymentMethodAPI(merchant?.code, request);
      window.location.href = data.url;
    }
  }

  // NOTE: ⚠️ saved for old subscriptions
  const isStripeSubscription = useMemo((): boolean => {
    return merchantSubscriptions?.currentSubscriptions?.some((subscription) => 
    subscription?.externalProviderCode === 'STRIPE' && !subscription.trial);
  }, [merchantSubscriptions?.currentSubscriptions]);

  return (
    <SubscriptionsContext.Provider
      value={{
        features: features,
        isFeaturesLoading: getMerchantFeaturesHook.isPending,
        currentPlan,
        subscriptions: merchantSubscriptions,
        subscriptionsAreLoading: getMerchantSubscriptionHook.isPending,
        subscriptionPlans,
        isPlansLoading: getAllSubscriptionHook.isPending,
        getPlanByPlanLevel,
        onSubscribeButtonClick,
        onUpdateToAnnualButtonClick,
        onCancelButtonClick,
        onUpdatePaymentMethod,
        planCurrency,
        setPlanCurrency,
        isStripeSubscription,
        availablePlanCurrencies,
        isLoadingCreateSub: createCheckoutSession?.isPending,
        isLoadingUpdateSub: updateSaasSubscription?.isPending,
        refetchSubscriptions,
      }}
    >
      {children}
    </SubscriptionsContext.Provider>
  );
}

export { SubscriptionsContext, SubscriptionsContextProvider };
